import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";

import ErrorMessage from "./ErrorMessage";

const RequiredMark = () => <span aria-hidden="true"> *</span>;

const GeneralReturnForm = ({ returnAPIBaseUrl, customerEmailLanguage, isJoLuOrder }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    orderNumber: "",
    customerEmail: "",
    firstName: "",
    lastName: "",
    street: "",
    houseNumber: "",
    zip: "",
    city: "",
    countryCode: "",
    articleNumber: "",
    productName: "",
    quantity: "1",
    reason: "",
    userComment: "",
  });

  const privacyPolicyUrl = t("privacyPolicy.link.url");
  const privacyPolicyText = t("privacyPolicy.link.text");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((currentFormData) => ({
      ...currentFormData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const requestBodyData = {
      orderNumber: formData.orderNumber,
      customerEmail: formData.customerEmail.toLowerCase(),
      customerEmailLanguage,
      advertisingMediumCode: isJoLuOrder ? "JLU" : null,
      customer: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: {
          street: formData.street,
          houseNumber: formData.houseNumber,
          zip: formData.zip,
          city: formData.city,
          countryCode: formData.countryCode.toUpperCase(),
        },
      },
      articles: [
        {
          articleNumber: formData.articleNumber || formData.productName,
          productName: formData.productName,
          reason: formData.reason || null,
          quantity: formData.quantity || 1,
        },
      ],
      userComment: formData.userComment,
    };

    try {
      await axios.post(`${returnAPIBaseUrl}/generalReturn`, {
        data: requestBodyData,
      });
      setSubmitted(true);
    } catch (submitError) {
      setError(submitError.message);
    }
  };

  if (submitted) {
    return (
      <div className="form-container">
        <p>{t("generalReturnForm.successMessage")}</p>
        <Button className="p-1 btn change-btn" type="button" onClick={() => navigate("/")}>
          {t("buttons.back")}
        </Button>
      </div>
    );
  }

  return (
    <div className="form-container general-return-form">
      <h1 className="h1">{t("generalReturnForm.heading")}</h1>
      {error && <ErrorMessage errorMessage={error} />}
      <Form className="general-return-form-grid" onSubmit={handleSubmit}>
        <Form.Group className="mb-3" controlId="generalReturnOrderNumber">
          <Form.Label>{t("generalReturnForm.orderNumberLabel")}</Form.Label>
          <Form.Control type="text" name="orderNumber" placeholder={t("generalReturnForm.orderNumberPlaceholder")} value={formData.orderNumber} onChange={handleChange} />
        </Form.Group>
        <Form.Group className="mb-3" controlId="generalReturnEmail">
          <Form.Label>
            {t("generalReturnForm.emailLabel")}
            <RequiredMark />
          </Form.Label>
          <Form.Control type="email" name="customerEmail" placeholder={t("generalReturnForm.emailPlaceholder")} value={formData.customerEmail} onChange={handleChange} required />
        </Form.Group>
        <Form.Group className="mb-3" controlId="generalReturnFirstName">
          <Form.Label>
            {t("generalReturnForm.firstNameLabel")}
            <RequiredMark />
          </Form.Label>
          <Form.Control type="text" name="firstName" placeholder={t("generalReturnForm.firstNamePlaceholder")} value={formData.firstName} onChange={handleChange} required />
        </Form.Group>
        <Form.Group className="mb-3" controlId="generalReturnLastName">
          <Form.Label>{t("generalReturnForm.lastNameLabel")}</Form.Label>
          <Form.Control type="text" name="lastName" placeholder={t("generalReturnForm.lastNamePlaceholder")} value={formData.lastName} onChange={handleChange} />
        </Form.Group>
        <Form.Group className="mb-3" controlId="generalReturnStreet">
          <Form.Label>{t("generalReturnForm.streetLabel")}</Form.Label>
          <Form.Control type="text" name="street" placeholder={t("generalReturnForm.streetPlaceholder")} value={formData.street} onChange={handleChange} />
        </Form.Group>
        <Form.Group className="mb-3" controlId="generalReturnHouseNumber">
          <Form.Label>{t("generalReturnForm.houseNumberLabel")}</Form.Label>
          <Form.Control type="text" name="houseNumber" placeholder={t("generalReturnForm.houseNumberPlaceholder")} value={formData.houseNumber} onChange={handleChange} />
        </Form.Group>
        <Form.Group className="mb-3" controlId="generalReturnZip">
          <Form.Label>{t("generalReturnForm.zipLabel")}</Form.Label>
          <Form.Control type="text" name="zip" placeholder={t("generalReturnForm.zipPlaceholder")} value={formData.zip} onChange={handleChange} />
        </Form.Group>
        <Form.Group className="mb-3" controlId="generalReturnCity">
          <Form.Label>{t("generalReturnForm.cityLabel")}</Form.Label>
          <Form.Control type="text" name="city" placeholder={t("generalReturnForm.cityPlaceholder")} value={formData.city} onChange={handleChange} />
        </Form.Group>
        <Form.Group className="mb-3" controlId="generalReturnCountryCode">
          <Form.Label>{t("generalReturnForm.countryCodeLabel")}</Form.Label>
          <Form.Control type="text" name="countryCode" placeholder={t("generalReturnForm.countryCodePlaceholder")} value={formData.countryCode} onChange={handleChange} />
        </Form.Group>
        <Form.Group className="mb-3" controlId="generalReturnArticleNumber">
          <Form.Label>{t("generalReturnForm.articleNumberLabel")}</Form.Label>
          <Form.Control type="text" name="articleNumber" placeholder={t("generalReturnForm.articleNumberPlaceholder")} value={formData.articleNumber} onChange={handleChange} />
        </Form.Group>
        <Form.Group className="mb-3 general-return-form-full" controlId="generalReturnProductName">
          <Form.Label>
            {t("generalReturnForm.productNameLabel")}
            <RequiredMark />
          </Form.Label>
          <Form.Control type="text" name="productName" placeholder={t("generalReturnForm.productNamePlaceholder")} value={formData.productName} onChange={handleChange} required />
        </Form.Group>
        <Form.Group className="mb-3" controlId="generalReturnQuantity">
          <Form.Label>
            {t("generalReturnForm.quantityLabel")}
            <RequiredMark />
          </Form.Label>
          <Form.Control type="number" min="1" name="quantity" placeholder={t("generalReturnForm.quantityPlaceholder")} value={formData.quantity} onChange={handleChange} required />
        </Form.Group>
        <Form.Group className="mb-3" controlId="generalReturnReason">
          <Form.Label>{t("generalReturnForm.reasonLabel")}</Form.Label>
          <Form.Control type="text" name="reason" placeholder={t("generalReturnForm.reasonPlaceholder")} value={formData.reason} onChange={handleChange} />
        </Form.Group>
        <Form.Group className="mb-3 general-return-form-full" controlId="generalReturnComment">
          <Form.Label>{t("generalReturnForm.commentLabel")}</Form.Label>
          <Form.Control as="textarea" rows={4} name="userComment" placeholder={t("generalReturnForm.commentPlaceholder")} value={formData.userComment} onChange={handleChange} />
        </Form.Group>
        <div className="general-return-actions general-return-form-full">
          <Button className="btn" variant="dark" type="submit">
            {t("generalReturnForm.submitButton")}
          </Button>
          <Button className="p-1 btn change-btn" type="button" onClick={() => navigate("/")}>
            {t("buttons.cancel")}
          </Button>
        </div>
        <p className="privacy-policy-notice general-return-form-full">
          <a href={privacyPolicyUrl} target="_blank" rel="noreferrer">
            {privacyPolicyText}
          </a>
        </p>
      </Form>
    </div>
  );
};

export default GeneralReturnForm;
